/* eslint-disable no-console */
/* eslint-disable func-names */
/*
*Author: Shri Kant
 */

import { testMicrophone } from './testWebRtc/mictest';
import { testCamera } from './testWebRtc/camresolutionstest';
import { testNetwork } from './testWebRtc/nettest';
import { testConnectivity } from './testWebRtc/conntest';
import { testThroughPut } from './testWebRtc/bandwidth_test';
import { testSignalling } from './testSignallingServer/signalling';
import Logger from './utils/Logger';
import { EventDispatcher, ClientHealthEvent } from './Events'; // This package is needed for managing events
import config from './../Common.config';

const util = require('./testWebRtc/util.js');

const PREFIX_INFO = '[ INFO ]';
const PREFIX_OK = '[ OK ]';
const PREFIX_FAILED = '[ FAILED ]';
const PREFIX_WARNING = '[ WARN ]';
const PREFIX_POOR = '[ POOR ]';
const PREFIX_ACCEPTABLE = '[ MODERATE ]';
const PREFIX_GOOD = '[ GOOD ]';

const MICROPHONE = 'microphone';
const CAMERA = 'camera';
const NETWORK = 'network';
const CONNECTIVITY = 'connectivity';
const THROUGHPUT = 'throughput';
const BANDWIDTH = 'bandwidth';
const SIGNALLING = 'signalling';
const AVAILABLE_REGION = ['IN','US', 'SG', 'DE'];

const MAX_BANDWIDTH_TEST_DURATION = 60;
const BITRATE_VIDEO_TEST_DURATION = 40;
const BITRATE_AUDIO_TEST_DURATION = 20;

const clientHealth = {};
let index = 0;
let testCases = [];
let responseMicrophone = {};
let responseCamera = {};
let responseNetwork = {};
let responseConnectivity = {};
let responseThroughPut = {};
let responseBandwidth = {};
let responseSignalling = {};
let audioDeviceId = '';
let videoDeviceId = '';
let regionId = [];
let running = false;
let bitrateOnly = false;
let eventDispatcher = null;
let testServerApiHost = '';
let testServerApiPort = '';
let forMobile = false;
let testDurationDataThroughput = 0;
let testDurationVideoBandwidth = 0;
let testDurationAudioBandwidth = 0;
let forIosVersion13 = false;
/* This API reset all the global variables */
function reset() {
  index = 0;
  testCases.splice(0, testCases.length);
  responseMicrophone = {};
  responseCamera = {};
  responseNetwork = {};
  responseConnectivity = {};
  responseThroughPut = {};
  responseBandwidth = {};
  responseSignalling = {};
  videoDeviceId = '';
  audioDeviceId = '';
  regionId = config.default_region;
  testServerApiHost = config.proxy_api_url;
  testServerApiPort = config.proxy_api_port;
  forMobile = false;
  bitrateOnly = false;
  testDurationDataThroughput = config.data_throughput_duration;
  testDurationVideoBandwidth = config.video_bandwidth_duration;
  testDurationAudioBandwidth = config.audio_bandwidth_duration;
}

/*
 * Class: TestRTC
 * Description: This class create a test case. It collects all the results e.g error, success,
 * warning etc while test case is in execution.
 * This class also collect the data and prepares the JSON object for the output.
 * When test case completed it calls the completion callback.
 */
class TestRTC {
  constructor(name, group, cb) {
    this.test_title = name;
    this.test_group = group;
    this.started = false;
    this.error_count = 0;
    this.warning_count = 0;
    this.success_count = 0;
    this.finished_cb = cb;
    this.region = 'IN';
    this.server = 'TURN';
    this.uri = '';
    this.progress_value = 0;
    this.state = 'not_tested';
    this.start_time = 0;
    this.bitrate_only = false;
    this.json_response = {
      result: this.state,
      execution_time: 'not_set',
      success: [],
      error: [],
      warning: [],
      info: [],
    };

    /* This API marks that  test case is executed */
    this.done = function () {
      const success = (this.error_count + this.warning_count === 0 && this.success_count > 0);
        if (success) {
          this.state = 'success';
        } else if (this.warning_count > 0 && this.error_count === 0) {
          this.state = 'warning';
        } else if (this.isDisabled) {
          this.state = 'disabled';
        } else {
          this.state = 'failure';
        }

        this.json_response.result = this.state;
        let now = new Date();
        this.json_response.execution_time = `${(now - this.start_time)} ms`;
        let current_response = { };
        switch (this.test_group) {
          case MICROPHONE:
            responseMicrophone[this.test_title] = this.json_response;
            current_response[this.test_title] = responseMicrophone[this.test_title];
            break;
          case CAMERA:
            responseCamera[this.test_title] = this.json_response;
            current_response[this.test_title] = responseCamera[this.test_title];
            break;
          case NETWORK:
            this.setServer();
            current_response = this.fillResponse(responseNetwork);
            break;
          case CONNECTIVITY:
            this.setServer();
            current_response = this.fillResponse(responseConnectivity);
            break;
          case THROUGHPUT:
            this.setServer();
            current_response = this.fillResponse(responseThroughPut);
            break;
          case BANDWIDTH:
            this.setServer();
            current_response = this.fillResponse(responseBandwidth);
            break;
          case SIGNALLING:
              this.setServer();
            current_response = this.fillResponse(responseSignalling);
              break;
            default:
              break;
        }
        const msg = {
          test_group: this.test_group,
          test_case: this.test_title,
          status: 'finished',
          output: current_response
        };
        if(this.test_group === NETWORK || this.test_group === CONNECTIVITY || this.test_group === THROUGHPUT
          || this.test_group === BANDWIDTH || this.test_group === SIGNALLING) {
            msg['test_region'] = this.region;
            msg['test_server'] = this.server;
          }
        const connectEvt = ClientHealthEvent({ type: 'client-diagnosis-status', message: msg });
        if(forIosVersion13 === true) {
          window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
        } else {
          eventDispatcher.dispatchEvent(connectEvt);
        }
        this.finished_cb();
    };

    /* Internal API */
    this.fillResponse = function (testResponse) {
      let response = { };
      if (!testResponse.hasOwnProperty(this.test_title)) {
        testResponse[this.test_title] = {};
      }
      if (!(testResponse[this.test_title].hasOwnProperty(this.region))) {
        testResponse[this.test_title][this.region] = {};
      }
      if (!(testResponse[this.test_title][this.region].hasOwnProperty(this.server))) {
        testResponse[this.test_title][this.region][this.server] = {};
      }
      testResponse[this.test_title][this.region][this.server] = this.json_response;
      response[this.test_title] = this.json_response;
      //response[this.test_title][this.region] = {};
      //response[this.test_title][this.region][this.server] = this.json_response;
      return response;
    };

    /* This API reports the fatal events */
    this.reportFatal = function (str) {
      this.reportError(str);
      this.done();
    };

    /* This API reports the success events */
    this.reportSuccess = function (str) {
      this.success_count += 1;
      Logger.info(PREFIX_OK + str);
      this.json_response.success.push({ message: str });
    };

    /* This API adds the server info in json */
    this.setServer = function () {
      this.json_response.server = {};
      this.json_response.server.region = this.region;
      if (this.server === 'TURN') {
        this.json_response.server.url = config.ice_servers[this.region].turnURI;
      } else {
        this.json_response.server.url = config.ice_servers[this.region].stunURI;
      }
    };

    /* This API reports the error events */
    this.reportError = function (str) {
      this.error_count += 1;
      Logger.error(PREFIX_FAILED + str);
      this.json_response.error.push({ message: str });
    };

    /* This API reports the warning events */
    this.reportWarning = function (str) {
      this.warning_count += 1;
      Logger.warning(PREFIX_WARNING + str);
      this.json_response.warning.push({ message: str });
    };

    /* This API reports the info events */
    this.reportInfo = function (str) {
      Logger.info(PREFIX_INFO + str);
      this.json_response.info.push({message: str});
    };

    this.fillBandwidth = function (str) {
      this.json_response['bandwidth_bps'] = str;
    };
    this.fillPacketsLost = function (str) {
      this.json_response['packets_lost_percentage'] = str;
    };

    /* This API update the status of test case in execution */
    this.setProgress = function (value) {
      this.progress_value = value;
    };

    this.fillData = function (X, Y) {
      //console.log('time' + X + ',value = '+ Y);
    };

    /* This API reports the events based upon their state */
    this.reportByState = function (str, state) {
      switch (state) {
        case 'poor':
          this.error_count += 1;
          Logger.error(PREFIX_POOR + str);
          this.json_response.error.push({ message: `${str} [BAD]`});
          break;
        case 'moderate':
          this.warning_count += 1;
          Logger.warning(PREFIX_ACCEPTABLE + str);
          this.json_response.warning.push({ message: `${str} [ACCEPTABLE]`});
          break;
        case 'good':
          this.success_count += 1;
          Logger.info(PREFIX_GOOD + str);
          this.json_response.success.push({ message: `${str} [GOOD]`});
          break;
        default:
          break;
      }
    };
  }
}

/* This API executes the selected test case */
function runTestCase(test) {
  util.setActiveTest(test);
  const msg = {
    test_group: test.test_group,
    test_case: test.test_title,
    status: 'started'
  };
  if(test.test_group === NETWORK || test.test_group === CONNECTIVITY || test.test_group === THROUGHPUT
    || test.test_group === BANDWIDTH || test.test_group === SIGNALLING) {
      msg['test_region'] = test.region;
      msg['test_server'] = test.server;
    }
  test.start_time = new Date();
  const connectEvt = ClientHealthEvent({ type: 'client-diagnosis-status', message: msg });
  if(forIosVersion13 === true) {
    window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
  } else {
    eventDispatcher.dispatchEvent(connectEvt);
  }
  
  switch (test.test_title) {
    case 'audio_capture': {
      testMicrophone(test, audioDeviceId);
      break;
    }
    case 'check_resolution_240':
    case 'check_resolution_480':
    case 'check_resolution_720':
    case 'check_supported_resolutions': {
      testCamera(test, videoDeviceId);
      break;
    }
    case 'udp_enabled':
    case 'tcp_enabled':
    case 'ipv6_enabled': {
      testNetwork(test);
      break;
    }
    case 'relay_connectivity':
    case 'reflexive_connectivity':
    case 'host_connectivity': {
      testConnectivity(test);
      break;
    }
    case 'data_throughput':{
      testThroughPut(test, null, testDurationDataThroughput);
      break;
    }
    case 'audio_bandwidth':{
      testThroughPut(test, audioDeviceId, testDurationAudioBandwidth);
      break;
    }
    case 'video_bandwidth': {
      testThroughPut(test, videoDeviceId, testDurationVideoBandwidth);
      break;
    }
    case 'signalling_connectivity': {
      testSignalling(test, testServerApiHost, testServerApiPort);
      break;
    }
    default:
      break;
  }

}

/* This API executes the selected test case */
function runNextTestCase() {
  if(running === false) {
    clientDiagnosticsStopped();
    return;
  }
  if (index < testCases.length) {
    const test = testCases[index];
    runTestCase(test);
    index += 1;
  } else {
    const jsonResponse = { };
    if (Object.keys(responseMicrophone).length) {
      jsonResponse[MICROPHONE] = responseMicrophone;
    }
    if (Object.keys(responseCamera).length) {
      jsonResponse[CAMERA] = responseCamera;
    }
    if (Object.keys(responseNetwork).length) {
      jsonResponse[NETWORK] = responseNetwork;
    }
    if (Object.keys(responseConnectivity).length) {
      jsonResponse[CONNECTIVITY] = responseConnectivity;
    }
    if (Object.keys(responseThroughPut).length) {
      jsonResponse[THROUGHPUT] = responseThroughPut;
    }
    if (Object.keys(responseBandwidth).length) {
      jsonResponse[BANDWIDTH] = responseBandwidth;
    }
    if (Object.keys(responseSignalling).length) {
      jsonResponse[SIGNALLING] = responseSignalling;
    }

    running = false;
    Logger.info('All test case executed');


    const connectEvt = ClientHealthEvent({ type: 'client-diagnosis-finished', message: jsonResponse });
    if(forIosVersion13 === true) {
      window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
    } else {
      eventDispatcher.dispatchEvent(connectEvt);
    }
    reset();
  }
}

/* This API add the test case in list */
function addTestCase(testName, testGroup, region = null, server = null, bitrate = false) {
  const test = new TestRTC(testName, testGroup, runNextTestCase);
  test.region = region;
  test.server = server;
  test.bitrate_only = bitrate;
  testCases.push(test);
}

/* This API creates the microphone related test cases */
function clientHealthMicrophone() {
  addTestCase('audio_capture', MICROPHONE);
}

/* This API creates the camera related test cases */
function clientHealthCamera() {
  if(forMobile === true) {
    addTestCase('check_supported_resolutions', CAMERA);
  } else {
    addTestCase('check_resolution_240', CAMERA);
    addTestCase('check_resolution_480', CAMERA);
    addTestCase('check_resolution_720', CAMERA);
    addTestCase('check_supported_resolutions', CAMERA);
  }
}

/* This API creates the network related test cases */
function clientHealthNetwork() {
  for (const index in regionId) {
    if(AVAILABLE_REGION.includes(regionId[index])) {
      addTestCase('udp_enabled', NETWORK, regionId[index], 'TURN');
      //addTestCase('udp_enabled', NETWORK, regionId[index], 'STUN');
      addTestCase('tcp_enabled', NETWORK, regionId[index], 'TURN');
      //addTestCase('tcp_enabled', NETWORK, regionId[index], 'STUN');
      addTestCase('ipv6_enabled', NETWORK, regionId[index], 'TURN');
      //addTestCase('ipv6_enabled', NETWORK, regionId[index], 'STUN');
    }
  }
}

/* This API creates the connectivity related test cases */
function clientHealthConnectivity() {
  for (const index in regionId) {
    if(AVAILABLE_REGION.includes(regionId[index])) {
      addTestCase('relay_connectivity', CONNECTIVITY, regionId[index], 'TURN');
      addTestCase('reflexive_connectivity', CONNECTIVITY, regionId[index], 'TURN');
      //addTestCase('reflexive_connectivity', CONNECTIVITY, regionId[index], 'STUN');
      addTestCase('host_connectivity', CONNECTIVITY, regionId[index], 'TURN');
      //addTestCase('host_connectivity', CONNECTIVITY, regionId[index], 'STUN');
    }
  }
}

/* This API creates the throughput related test cases */
function clientHealthThroughput() {
  for (const index in regionId) {
    if(AVAILABLE_REGION.includes(regionId[index])) {
      addTestCase('data_throughput', THROUGHPUT, regionId[index], 'TURN');
    }
  }
}

/* This API creates the bandwidth related test cases */
function clientHealthBandwidth() {
  for (const index in regionId) {
    if(AVAILABLE_REGION.includes(regionId[index])) {
      addTestCase('video_bandwidth', BANDWIDTH, regionId[index], 'TURN', bitrateOnly);
      addTestCase('audio_bandwidth', BANDWIDTH, regionId[index], 'TURN', bitrateOnly);
    }
  }
}

/* This API creates the signalling related test cases */
function clientHealthSignalling() {
  for (const index in regionId) {
    if(AVAILABLE_REGION.includes(regionId[index])) {
      addTestCase('signalling_connectivity', SIGNALLING, regionId[index], 'TURN');
    }
  }
}

/* This API creates begin the execution of test cases*/
function executeTestCases() {
  setTimeout(runNextTestCase, 1);
}

/*

var Options = {
     testNames: ['microphone', 'camera'],
     audioDeviceId: 'XXX',
     videoDeviceId: 'XXX',
     regionId: ['IN','US'],
     testServerApiHost: 'api-devvm47.vcloudx.com',
     testServerApiPort: '8626',
     stop: 'false'
 }

client = EnxRtc.clientDiagnostics(Options);
client.addEventListener("client-diagnosis-finished", function(response) {
     // handle json from response.message
});

client.addEventListener("client-diagnosis-failed", function(response) {
     // handle error message from response.message
});

client.addEventListener("client-diagnosis-status", function(response) {
     // handle info message from response.message
});

client.addEventListener('client-diagnosis-stopped', function(response) {
   // handle info message from response.message
});

 */
clientHealth.clientDiagnostics = (clientInfo) => {
  forIosVersion13 = false;
  if (clientInfo.hasOwnProperty('forIosVersion13') && clientInfo.forIosVersion13 === true) {
    forIosVersion13 = true;
  }
  if (clientInfo.hasOwnProperty('stop') && clientInfo.stop === true && eventDispatcher !== null) {
    clientDiagnosticsStop();
    return eventDispatcher;
  }
  if (running || testCases.length) {
    Logger.error('Diagnosis is already in progress. Few resources are in use and may not be accessible.');
    return eventDispatcher;
  }
  eventDispatcher = EventDispatcher();

  if (!clientInfo.hasOwnProperty('testNames')) {
    setTimeout(diagnosisFailed, 1);
    return eventDispatcher;
  }
  if(!Array.isArray(clientInfo.testNames))
  {
    setTimeout(diagnosisFailed, 1);
    return eventDispatcher;
  }
  running = true;



  reset();

  if (clientInfo.hasOwnProperty('audioDeviceId')) {
    audioDeviceId = clientInfo.audioDeviceId;
  }
  if (clientInfo.hasOwnProperty('videoDeviceId')) {
    videoDeviceId = clientInfo.videoDeviceId;
  }
  if (clientInfo.hasOwnProperty('testServerApiHost')) {
    testServerApiHost = clientInfo.testServerApiHost;
  }
  if (clientInfo.hasOwnProperty('testServerApiPort')) {
    testServerApiPort = clientInfo.testServerApiPort;
  }
  if (clientInfo.hasOwnProperty('regionId')) {
    if(Array.isArray(clientInfo.regionId)) {
      regionId = clientInfo.regionId || config.default_region;
    } else {
      running = false;
      setTimeout(diagnosisFailed, 1);
      return eventDispatcher;
    }
  }
  if (clientInfo.hasOwnProperty('forMobile') && clientInfo.forMobile === true) {
    forMobile = true;
  }
  if (clientInfo.hasOwnProperty('bitrateOnly') && clientInfo.bitrateOnly === true) {
    bitrateOnly = true;
  }
  if (clientInfo.hasOwnProperty('testDurationDataThroughput') && clientInfo.testDurationDataThroughput > 0) {
    testDurationDataThroughput = clientInfo.testDurationDataThroughput;
  }
  if(testDurationDataThroughput > MAX_BANDWIDTH_TEST_DURATION) {
    testDurationDataThroughput = MAX_BANDWIDTH_TEST_DURATION;
  }
  if (clientInfo.hasOwnProperty('testDurationVideoBandwidth') && clientInfo.testDurationVideoBandwidth > 0) {
    testDurationVideoBandwidth = clientInfo.testDurationVideoBandwidth;
  }
  if(testDurationVideoBandwidth > MAX_BANDWIDTH_TEST_DURATION) {
    testDurationVideoBandwidth = MAX_BANDWIDTH_TEST_DURATION;
  }
  if (clientInfo.hasOwnProperty('testDurationAudioBandwidth') && clientInfo.testDurationAudioBandwidth > 0) {
    testDurationAudioBandwidth = clientInfo.testDurationAudioBandwidth;
  }
  if(testDurationAudioBandwidth > MAX_BANDWIDTH_TEST_DURATION) {
    testDurationAudioBandwidth = MAX_BANDWIDTH_TEST_DURATION;
  }

  const testNames = clientInfo.testNames.map(function(item){ item = item.toString(); return item.toLowerCase(); })
  const testGroupSet = new Set();
  if(testNames.includes('all')) {
      testGroupSet.add(MICROPHONE);
      testGroupSet.add(CAMERA);
      testGroupSet.add(NETWORK);
      testGroupSet.add(CONNECTIVITY);
      testGroupSet.add(THROUGHPUT);
      testGroupSet.add(BANDWIDTH);
      testGroupSet.add(SIGNALLING);
  } else {
    for (const item in clientInfo.testNames) {
      testGroupSet.add(testNames[item]);
    }
  }
  for (const testName of testGroupSet) {
    switch (testName) {
      case MICROPHONE: {
        clientHealthMicrophone();
        break;
      }
      case CAMERA: {
        clientHealthCamera();
        break;
      }
      case NETWORK: {
        clientHealthNetwork();
        break;
      }
      case CONNECTIVITY: {
        clientHealthConnectivity();
        break;
      }
      case THROUGHPUT: {
        clientHealthThroughput();
        break;
      }
      case BANDWIDTH: {
        clientHealthBandwidth();
        break;
      }
      case SIGNALLING: {
        clientHealthSignalling();
        break;
      }      
      default: {
        // do nothing
      }
    }
  }

  if(testCases.length > 0) {
    executeTestCases();
  } else {
    running = false;
    setTimeout(diagnosisFailed, 1);
    return eventDispatcher;
  }
  return eventDispatcher;
};

/* This API stops the execution of test cases */
function clientDiagnosticsStop() {
  if (running === false && testCases.length === 0) {
    clientDiagnosticsStopped();
  }
  running = false;
  return eventDisp;
};

function diagnosisFailed() {
  const connectEvt = ClientHealthEvent({ type: 'client-diagnosis-failed', message: {msg: 'Invalid arguments' }});
  if(forIosVersion13 === true) {
    window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
  } else {
    eventDispatcher.dispatchEvent(connectEvt);
  }
  reset();
  Logger.error('Invalid argument');
}

function clientDiagnosticsStopped() {
  const connectEvt = ClientHealthEvent({ type: 'client-diagnosis-stopped', message: {msg: 'Diagnosis stopped.' }});
  if(forIosVersion13 === true) {
    window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
  } else {
    eventDispatcher.dispatchEvent(connectEvt);
  }
  reset();
  Logger.error('diagnosis stopped');
};

clientHealth.clientBitrate = (regionInfo) => {
  let eventDispatcher_bitrate = EventDispatcher();
  let default_region = 'DE';
  if (regionInfo !== undefined && regionInfo.hasOwnProperty('region')) {
    let region = regionInfo.region;
    if(AVAILABLE_REGION.includes(region)) {
      default_region = region;
    }
  }
  let client = EnxRtc.clientDiagnostics({
    testNames: ['bandwidth'],
    regionId: [default_region],
    testDurationVideoBandwidth: BITRATE_VIDEO_TEST_DURATION,
    testDurationAudioBandwidth: BITRATE_AUDIO_TEST_DURATION,
    bitrateOnly: true,
    stop: false,
  });
  let bitrate_result_json = {
    video: {
      bitrate_bps: 'NA',
      connection: 'NA',
    },
    audio:
    {
      bitrate_bps: 'NA',
      connection: 'NA',
    }
  };
  client.addEventListener("client-diagnosis-status", function(event) {
    const connectEvt = ClientHealthEvent({ type: 'client-bitrate-status', message: event.message });
    if(event.message.hasOwnProperty('output')) {
      if(event.message.output.hasOwnProperty('video_bandwidth')) {
        if(event.message.output.video_bandwidth.hasOwnProperty('bandwidth_bps')) {
          bitrate_result_json.video.bitrate_bps = event.message.output.video_bandwidth.bandwidth_bps;
        }
        if(event.message.output.video_bandwidth.hasOwnProperty('packets_lost_percentage')) {
          bitrate_result_json.video.connection = (event.message.output.video_bandwidth.packets_lost_percentage) > 5 ? 'unstable': 'stable';
        }
      }
      if(event.message.output.hasOwnProperty('audio_bandwidth')) {
        if(event.message.output.audio_bandwidth.hasOwnProperty('bandwidth_bps')) {
          bitrate_result_json.audio.bitrate_bps = event.message.output.audio_bandwidth.bandwidth_bps;
        }
        if(event.message.output.audio_bandwidth.hasOwnProperty('packets_lost_percentage')) {
          bitrate_result_json.audio.connection = (event.message.output.audio_bandwidth.packets_lost_percentage) > 5 ? 'unstable': 'stable';
        }
      }
    }
    if(forIosVersion13 === true) {
      window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
    } else {
      eventDispatcher_bitrate.dispatchEvent(connectEvt);
    }
  });
   client.addEventListener("client-diagnosis-finished", function(event) {
    const connectEvt = ClientHealthEvent({ type: 'client-bitrate-finished', message: bitrate_result_json });
    if(forIosVersion13 === true) {
      window.webkit.messageHandlers.callbackHandler.postMessage(connectEvt);
    } else {
      eventDispatcher_bitrate.dispatchEvent(connectEvt);
    }
   });
  return eventDispatcher_bitrate;
};

export default clientHealth;



//////////////////
// WEBPACK FOOTER
// ./src/clientHealth.js
// module id = 205
// module chunks = 0